import { useEffect, useState } from "react";

import { fetchHalls, refreshHalls } from "../api";
import { fromCompact } from "../plan";
import type { Cinema, Hall } from "../types";
import { Dialog } from "./Dialog";
import { SeatPlan } from "./SeatPlan";

type Props = {
  cinema: Cinema;
  date: string;
  onClose: () => void;
};

export function HallsDialog({ cinema, date, onClose }: Props) {
  const [halls, setHalls] = useState<Hall[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const load = () => {
      fetchHalls(cinema.slug, date)
        .then((catalog) => {
          if (cancelled) return;
          setHalls(catalog.halls);
          setFilling(!!catalog.filling);
          // le relevé tourne côté serveur : on redemande jusqu'à ce qu'il ait fini
          if (catalog.filling) timer = window.setTimeout(load, 4000);
        })
        .catch((cause: Error) => !cancelled && setError(cause.message));
    };

    load();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cinema.slug, date]);

  const complete = async () => {
    setFilling(true);
    setError(null);
    try {
      const catalog = await refreshHalls(cinema.slug, date);
      setHalls(catalog.halls);
      window.setTimeout(() => fetchHalls(cinema.slug, date).then((c) => {
        setHalls(c.halls);
        setFilling(!!c.filling);
      }), 5000);
    } catch (cause) {
      setError((cause as Error).message);
      setFilling(false);
    }
  };

  const known = halls?.length ?? 0;
  const missing = Math.max(0, cinema.hallCount - known);

  return (
    <Dialog
      title={`Les salles de ${cinema.name}`}
      subtitle={
        halls
          ? `${known} salle${known > 1 ? "s" : ""} en mémoire sur ${cinema.hallCount} · ${cinema.seatCount} places au total`
          : "lecture du catalogue…"
      }
      width="wide"
      onClose={onClose}
      footer={
        <>
          <span className="state">
            {filling
              ? "Relevé des salles en cours — la liste se complète toute seule."
              : missing > 0
              ? `${missing} salle${missing > 1 ? "s" : ""} pas encore relevée${missing > 1 ? "s" : ""} — elles s'ajoutent au fil des consultations.`
              : "Catalogue complet."}
          </span>
          <span className="grow" />
          {missing > 0 && (
            <button type="button" className="btn btn-primary" onClick={complete} disabled={filling}>
              {filling ? "Relevé en cours…" : "Compléter"}
            </button>
          )}
        </>
      }
    >
      {error && <p className="state is-error">{error}</p>}

      {!halls && !error && (
        <div className="loading">
          <div className="spinner" />
          <span className="state">lecture du catalogue…</span>
        </div>
      )}

      {halls && halls.length === 0 && (
        <div className="empty">
          <p className="empty-title">Aucune salle en mémoire</p>
          <p className="empty-text">
            Consultez une séance, ou lancez « Compléter » pour relever les salles de ce cinéma.
          </p>
        </div>
      )}

      {halls && halls.length > 0 && (
        <div className="hall-cards">
          {halls.map((hall) => (
            <article className="hall-card is-static" key={hall.auditorium}>
              <span className="hall-card-top">
                <span className="hall-card-time">Salle {hall.auditorium}</span>
                <span className="hall-card-fill">
                  {hall.capacity} places · {hall.plan.rows.length} rangées
                </span>
              </span>
              <SeatPlan plan={fromCompact(hall.plan)} variant="mini" />
            </article>
          ))}
        </div>
      )}
    </Dialog>
  );
}
